import { Shield, Users, Star, Target, Eye, Heart } from "lucide-react";
import { Helmet } from "react-helmet-async";

const values = [
  {
    icon: Shield,
    title: "Trust & Transparency",
    description: "We provide honest, unbiased reviews based on thorough research and real player experiences.",
  },
  {
    icon: Users,
    title: "Player First",
    description: "Every review and recommendation is made with the player's best interest in mind.",
  },
  {
    icon: Star,
    title: "Quality Standards",
    description: "We only feature casinos that meet our strict criteria for safety, fairness, and quality.",
  },
  {
    icon: Target,
    title: "Accuracy",
    description: "Our information is regularly updated to ensure accuracy and relevance.",
  },
];

const team = [
  { name: "Alex Rivera", role: "Founder & CEO", description: "10+ years in iGaming industry" },
  { name: "Sarah Chen", role: "Head of Reviews", description: "Casino expert & analyst" },
  { name: "Marcus Johnson", role: "Content Director", description: "Gaming journalist" },
  { name: "Emma Williams", role: "Community Manager", description: "Player advocate" },
];

export default function About() {
  return (
    <>
      <Helmet>
        <title>About PlaySpiro – Trusted Casino Review Platform</title>
        <meta name="description" content="Learn about PlaySpiro, the trusted platform for online casino reviews trusted by 50,000+ players." />
        <link rel="canonical" href="https://playspiro.com/about" />
      </Helmet>
    <div className="min-h-screen py-8" data-testid="about-page">
      {/* Header */}
      <section className="gradient-hero py-16 mb-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-black text-white mb-4 font-['Montserrat']">
            About <span className="text-primary">PlaySpiro</span>
          </h1>
          <p className="text-white/60 max-w-2xl mx-auto text-lg">
            Your trusted guide to the world of online casinos since 2020.
          </p>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4">
        {/* Mission */}
        <section className="glass rounded-2xl p-8 md:p-12 mb-12 text-center">
          <Eye className="w-12 h-12 text-primary mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">Our Mission</h2>
          <p className="text-white/70 text-lg max-w-3xl mx-auto leading-relaxed">
            At PlaySpiro, we believe every player deserves access to honest, comprehensive information 
            about online casinos. Our mission is to empower players to make informed decisions by 
            providing trustworthy reviews, fair comparisons, and expert guidance in the ever-evolving 
            world of online gaming.
          </p>
        </section>

        {/* Story */}
        <section className="mb-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6 font-['Montserrat']">Our Story</h2>
              <div className="space-y-4 text-white/70">
                <p>
                  PlaySpiro was founded in 2020 by a group of passionate gamers who were frustrated 
                  with the lack of reliable information about online casinos. Too many review sites 
                  seemed biased or outdated, leaving players to figure things out on their own.
                </p>
                <p>
                  We set out to change that. Our team of industry experts, former casino employees, 
                  and dedicated gamers work together to bring you the most accurate, up-to-date, 
                  and honest casino reviews available.
                </p>
                <p>
                  Today, PlaySpiro serves over 50,000 monthly visitors, helping players find safe, 
                  fair, and enjoyable online gaming experiences. We're proud to be a trusted voice 
                  in the community.
                </p>
              </div>
            </div>
            <div className="glass rounded-2xl p-8">
              <div className="grid grid-cols-2 gap-6 text-center">
                <div>
                  <div className="text-4xl font-black text-primary mb-2">2020</div>
                  <p className="text-white/60 text-sm">Founded</p>
                </div>
                <div>
                  <div className="text-4xl font-black text-secondary mb-2">50K+</div>
                  <p className="text-white/60 text-sm">Monthly Visitors</p>
                </div>
                <div>
                  <div className="text-4xl font-black text-green-400 mb-2">100+</div>
                  <p className="text-white/60 text-sm">Casino Reviews</p>
                </div>
                <div>
                  <div className="text-4xl font-black text-orange-400 mb-2">10K+</div>
                  <p className="text-white/60 text-sm">Happy Players</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-white mb-8 text-center font-['Montserrat']">Our Values</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div key={index} className="glass rounded-xl p-6 text-center card-hover">
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-primary/20 flex items-center justify-center">
                  <value.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-white font-bold mb-2">{value.title}</h3>
                <p className="text-white/60 text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Team */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold text-white mb-8 text-center font-['Montserrat']">Our Team</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {team.map((member, index) => (
              <div key={index} className="glass rounded-xl p-6 text-center">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center text-white text-2xl font-bold">
                  {member.name.charAt(0)}
                </div>
                <h3 className="text-white font-bold">{member.name}</h3>
                <p className="text-primary text-sm mb-2">{member.role}</p>
                <p className="text-white/50 text-sm">{member.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Responsible Gambling */}
        <section className="glass rounded-2xl p-8 md:p-12 text-center border-2 border-green-500/30">
          <Heart className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-white mb-4 font-['Montserrat']">
            Commitment to Responsible Gambling
          </h2>
          <p className="text-white/70 max-w-3xl mx-auto mb-6">
            We are committed to promoting responsible gambling. We encourage all players to set limits, 
            take breaks, and seek help if gambling becomes a problem. All casinos we recommend offer 
            responsible gambling tools and resources.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <span className="px-4 py-2 bg-white/5 rounded-full text-white/60 text-sm">GamCare</span>
            <span className="px-4 py-2 bg-white/5 rounded-full text-white/60 text-sm">BeGambleAware</span>
            <span className="px-4 py-2 bg-white/5 rounded-full text-white/60 text-sm">Gamblers Anonymous</span>
          </div>
        </section>
      </div>
    </div>
      </>
  );
}